import React, { useState, useEffect } from 'react';
import Login from './Login';
import { Box, Card, CardContent, Typography, Button, Container, Snackbar, Alert, Stack, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText, TextField, Paper } from '@mui/material';
import LockClockIcon from '@mui/icons-material/LockClock';

import ProductList from './components/ProductList';
import ProductForm from './components/ProductForm';

import SaleForm from './components/SaleForm';
import SaleList from './components/SaleList';
import Cashier from './components/Cashier';

import HeaderNavbar from './components/HeaderNavbar';
import Sidebar from './components/Sidebar';


import Transactions from './components/Transactions';
import CashierSessions from './components/CashierSessions';
import Customers from './components/Customers';
import UnpaidBills from './components/UnpaidBills';
import DealerList from './components/DealerList';
import StockPurchase from './components/StockPurchase';
import Reports from './components/Reports';
import Settings from './components/Settings';
import InvoiceDialog from './components/InvoiceDialog';
import { useLowStockAlerts } from './utils/lowStockAlerts';
import { useLowStockSetting } from './utils/useLowStockSetting';


function App() {
  // Global product search state (for header search bar)
  const [search, setSearch] = useState("");
  const [user, setUserState] = useState(() => {
    const saved = localStorage.getItem('posUser');
    if (!saved) return null;
    try {
      const parsed = JSON.parse(saved);
      if (parsed.expiry && Date.now() > parsed.expiry) {
        localStorage.removeItem('posUser');
        return null;
      }
      return parsed;
    } catch {
      localStorage.removeItem('posUser');
      return null;
    }
  });

  // Sync user to localStorage with expiry (1 day)
  const setUser = (u) => {
    if (u) {
      const expiry = Date.now() + 24 * 60 * 60 * 1000; // 1 day
      const userWithExpiry = { ...u, expiry };
      localStorage.setItem('posUser', JSON.stringify(userWithExpiry));
      setUserState(userWithExpiry);
    } else {
      localStorage.removeItem('posUser');
      setUserState(null);
    }
  };
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // UI tab state
  const [tab, setTab] = useState('cashier');

  // Sales state
  const [sales, setSales] = useState([]);
  const [salesLoading, setSalesLoading] = useState(false);

  // Cashier Session state
  const [currentSession, setCurrentSession] = useState(null);
  const [openSessionDialog, setOpenSessionDialog] = useState(false);
  const [closeSessionDialog, setCloseSessionDialog] = useState(false);
  const [openingCashAmount, setOpeningCashAmount] = useState('');
  const [actualCashAmount, setActualCashAmount] = useState('');
  const [sessionNotes, setSessionNotes] = useState('');
  const [sessionLoading, setSessionLoading] = useState(false);

  // Low stock alert logic
  const lowStockEnabled = useLowStockSetting();
  const lowStockProducts = useLowStockAlerts(products, lowStockEnabled);
  const [saleFormOpen, setSaleFormOpen] = useState(false);
  const [lowStockDialogOpen, setLowStockDialogOpen] = useState(false);

  // Unpaid bills state
  const [unpaidBills, setUnpaidBills] = useState([]);
  const [loadingUnpaid, setLoadingUnpaid] = useState(false);
  const [unpaidError, setUnpaidError] = useState(null);

  // State for editing unpaid bill
  const [editUnpaidBill, setEditUnpaidBill] = useState(null);
  const [editUnpaidOpen, setEditUnpaidOpen] = useState(false);
  
  // Invoice dialog for completed sales
  const [showInvoice, setShowInvoice] = useState(false);
  const [invoiceSale, setInvoiceSale] = useState(null);

  // Helper: get token
  const getToken = () => user?.token;

  // Fetch products
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/products', {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      setProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load products', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Auto-logout if session expired while app is open
  useEffect(() => {
    if (user && user.expiry && Date.now() > user.expiry) {
      setUser(null);
      return;
    }
    if (user) fetchProducts();
    if (user) fetchSales();
    if (user) fetchCurrentSession();
  }, [user]);

  // Open cashier session dialog
  const handleOpenSession = () => {
    setOpenSessionDialog(true);
  };
  
  // Actual session opening with API call
  const handleConfirmOpenSession = async () => {
    if (!openingCashAmount || parseFloat(openingCashAmount) < 0) {
      setSnackbar({ open: true, message: 'Please enter a valid opening cash amount', severity: 'error' });
      return;
    }
    
    setSessionLoading(true);
    try {
      const res = await fetch('/api/cashier-sessions/open', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`
        },
        body: JSON.stringify({ openingCash: parseFloat(openingCashAmount) })
      });
      
      if (res.ok) {
        const data = await res.json();
        setCurrentSession(data);
        setOpenSessionDialog(false);
        setOpeningCashAmount('');
        setSnackbar({ open: true, message: 'Session opened successfully', severity: 'success' });
      } else {
        const error = await res.json();
        setSnackbar({ open: true, message: error.error || 'Failed to open session', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to open session', severity: 'error' });
    } finally {
      setSessionLoading(false);
    }
  };

  // Close cashier session
  const handleCloseSession = async () => {
    if (!actualCashAmount || parseFloat(actualCashAmount) < 0) {
      setSnackbar({ open: true, message: 'Please enter a valid actual cash amount', severity: 'error' });
      return;
    }
    
    setSessionLoading(true);
    try {
      const res = await fetch('/api/cashier-sessions/close', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`
        },
        body: JSON.stringify({
          actualCash: parseFloat(actualCashAmount),
          notes: sessionNotes
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setSnackbar({ 
          open: true, 
          message: `Session closed! Difference: $${data.cashDifference.toFixed(2)}`, 
          severity: 'success' 
        });
        setCurrentSession(null);
        setCloseSessionDialog(false);
        setActualCashAmount('');
        setSessionNotes('');
      } else {
        const error = await res.json();
        setSnackbar({ open: true, message: error.error || 'Failed to close session', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to close session', severity: 'error' });
    } finally {
      setSessionLoading(false);
    }
  };

  // Fetch current cashier session
  const fetchCurrentSession = async () => {
    if (!user?.token) return;
    try {
      const res = await fetch('/api/cashier-sessions/current', {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCurrentSession(data);
      } else {
        setCurrentSession(null);
      }
    } catch {
      setCurrentSession(null);
    }
  };

  // Fetch sales
  const fetchSales = async () => {
    setSalesLoading(true);
    try {
      const res = await fetch('/api/sales', {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      setSales(Array.isArray(data) ? data : []);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load sales', severity: 'error' });
    } finally {
      setSalesLoading(false);
    }
  };

  // Fetch unpaid bills from backend
  const fetchUnpaidBills = async () => {
    setLoadingUnpaid(true);
    setUnpaidError(null);
    try {
      const res = await fetch('/api/sales?paymentStatus=Pending', {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        setUnpaidBills(data);
      } else {
        setUnpaidBills([]);
        setUnpaidError('No data returned');
      }
    } catch {
      setUnpaidBills([]);
      setUnpaidError('Failed to fetch unpaid bills');
    } finally {
      setLoadingUnpaid(false);
    }
  };

  // Fetch unpaid bills when tab changes to 'unpaid'
  useEffect(() => {
    if (tab === 'unpaid' && user) {
      fetchUnpaidBills();
    }
  }, [tab, user]);

  // Create new sale
  const handleSaveSale = async (sale) => {
    // Check if there's an active cashier session
    if (!currentSession) {
      setSnackbar({ 
        open: true, 
        message: 'Please open a cashier session before making a sale', 
        severity: 'error' 
      });
      return null;
    }
    
    try {
      const res = await fetch('/api/sales', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(sale),
      });
      if (res.ok) {
        const savedSale = await res.json();
        setSnackbar({ open: true, message: 'Sale recorded', severity: 'success' });
        setSaleFormOpen(false);
        fetchSales();
        fetchProducts(); // update stock
        return savedSale; // Return the saved sale
      } else {
        const data = await res.json();
        setSnackbar({ open: true, message: data.error || 'Sale failed', severity: 'error' });
        return null;
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Sale failed', severity: 'error' });
      return null;
    }
  };

  // Add product
  const handleAdd = () => {
    setEditProduct(null);
    setFormOpen(true);
  };

  // Edit product
  const handleEdit = (product) => {
    setEditProduct(product);
    setFormOpen(true);
  };

  // Delete product
  const handleDelete = async (product) => {
    if (!window.confirm(`Delete product "${product.name}"?`)) return;
    try {
      const res = await fetch(`/api/products/${product._id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.ok) {
        setSnackbar({ open: true, message: 'Product deleted', severity: 'success' });
        fetchProducts();
      } else {
        const data = await res.json();
        setSnackbar({ open: true, message: data.error || 'Delete failed', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Delete failed', severity: 'error' });
    }
  };

  // Save (add/edit) product
  const handleSave = async (product) => {
    try {
      const isEdit = !!editProduct;
      const url = isEdit ? `/api/products/${editProduct._id}` : '/api/products';
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(product),
      });
      if (res.ok) {
        setSnackbar({ open: true, message: isEdit ? 'Product updated' : 'Product added', severity: 'success' });
        setFormOpen(false);
        fetchProducts();
      } else {
        const data = await res.json();
        setSnackbar({ open: true, message: data.error || 'Save failed', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Save failed', severity: 'error' });
    }
  };

  if (!user) {
    return <Login onLogin={setUser} />;
  }

  const handleLogout = () => setUser(null);

  // Enhanced nav logic for Customers tab
  const handleNav = label => {
  if (label === 'Sales') setTab('cashier');
  if (label === 'Inventory' || label === 'Product Management') setTab('products');
  if (label === 'Transactions') setTab('transactions');
  if (label === 'Cashier Sessions') setTab('cashier-sessions');
  if (label === 'Customers') setTab('customers');
  if (label === 'Dealers') setTab('dealers');
  if (label === 'Stock Purchase') setTab('stock-purchase');
  if (label === 'Reports') setTab('reports');
  if (label === 'Settings') setTab('settings');
  if (label === 'Unpaid Bills') setTab('unpaid');
  };

  const activeTabLabel =
  tab === 'cashier' ? 'Sales'
  : tab === 'products' ? 'Inventory'
  : tab === 'transactions' ? 'Transactions'
  : tab === 'cashier-sessions' ? 'Cashier Sessions'
  : tab === 'customers' ? 'Customers'
  : tab === 'dealers' ? 'Dealers'
  : tab === 'stock-purchase' ? 'Stock Purchase'
  : tab === 'reports' ? 'Reports'
  : tab === 'settings' ? 'Settings'
  : tab === 'unpaid' ? 'Unpaid Bills'
  : '';

  // Handler for Update Order button
  const updateUnpaidBill = (bill) => {
    setEditUnpaidBill(bill);
    setEditUnpaidOpen(true);
  };

  // Handler for saving updated unpaid bill
  const handleSaveUnpaid = async (updatedSale) => {
    try {
      const res = await fetch(`/api/sales/${editUnpaidBill._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(updatedSale),
      });
      if (res.ok) {
        const savedSale = await res.json();
        setSnackbar({ open: true, message: 'Order updated', severity: 'success' });
        
        // Close the edit dialog first
        setEditUnpaidOpen(false);
        setEditUnpaidBill(null);
        
        // Show invoice after a brief delay
        setTimeout(() => {
          setInvoiceSale(savedSale);
          setShowInvoice(true);
        }, 300);
        
        fetchUnpaidBills();
        fetchSales();
        return savedSale;
      } else {
        const data = await res.json();
        setSnackbar({ open: true, message: data.error || 'Update failed', severity: 'error' });
        return null;
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Update failed', severity: 'error' });
      return null;
    }
  };

  return (
    <>
      <HeaderNavbar
        search={search}
        onSearchChange={tab === 'cashier' ? () => {
          // For cashier tab, trigger the product search dialog
          const event = new CustomEvent('openProductSearch');
          window.dispatchEvent(event);
        } : setSearch}
        lowStockProducts={lowStockProducts}
        onLowStockViewAll={tab === 'cashier' || tab === 'products' ? () => setLowStockDialogOpen(true) : undefined}
        user={user}
        showSearch={tab === 'cashier' || tab === 'products'}
        lowStockDialogOpen={lowStockDialogOpen}
        setLowStockDialogOpen={setLowStockDialogOpen}
        currentSession={currentSession}
        onOpenSession={handleOpenSession}
        onCloseSession={() => setCloseSessionDialog(true)}
      />
  <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f6fa', pt: { xs: 7, sm: 8 } }}>
        <Sidebar
          cashierName={user?.user?.username || 'Cashier'}
          onLogout={handleLogout}
          onNav={handleNav}
          active={activeTabLabel}
          user={user}
        />
        <Box
          sx={{
            flex: 1,
            ml: { xs: '64px', sm: '180px', md: '220px', lg: '260px' },
            p: 0,
            transition: 'margin-left 0.2s',
          }}
        >
          <Container maxWidth={false} disableGutters sx={{ p: 0 }}>
            <Card sx={{ p: 0, boxShadow: 0, borderRadius: 0 }}>
              <CardContent sx={{ p: 0 }}>
                {/* Welcome user message moved to header */}
                
                {/* Open Session Dialog */}
                <Dialog open={openSessionDialog} onClose={() => setOpenSessionDialog(false)} maxWidth="sm" fullWidth>
                  <DialogTitle>Open Cashier Session</DialogTitle>
                  <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Enter the starting cash amount in the drawer to begin your shift.
                      </Typography>
                      <TextField
                        label="Opening Cash Amount"
                        type="number"
                        value={openingCashAmount}
                        onChange={e => setOpeningCashAmount(e.target.value)}
                        inputProps={{ min: 0, step: 0.01 }}
                        fullWidth
                        required
                        autoFocus
                        helperText="Enter the cash amount in the drawer"
                      />
                    </Stack>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setOpenSessionDialog(false)}>Cancel</Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleConfirmOpenSession}
                      disabled={!openingCashAmount || sessionLoading}
                    >
                      Open Session
                    </Button>
                  </DialogActions>
                </Dialog>
                
                {/* Close Session Dialog */}
                <Dialog open={closeSessionDialog} onClose={() => setCloseSessionDialog(false)} maxWidth="sm" fullWidth>
                  <DialogTitle>Close Cashier Session</DialogTitle>
                  <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Please count the cash in your drawer and enter the actual amount below.
                      </Typography>
                      {currentSession && (
                        <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                          <Typography variant="h6" fontWeight={600} gutterBottom>Session Summary</Typography>
                          <Stack spacing={1}>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography>Session Started:</Typography>
                              <Typography fontWeight={600}>
                                {new Date(currentSession.openedAt).toLocaleString()}
                              </Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography>Opening Cash:</Typography>
                              <Typography fontWeight={600}>${currentSession.openingCash.toFixed(2)}</Typography>
                            </Stack>
                          </Stack>
                        </Paper>
                      )}
                      <TextField
                        label="Actual Cash in Drawer"
                        type="number"
                        value={actualCashAmount}
                        onChange={e => setActualCashAmount(e.target.value)}
                        inputProps={{ min: 0, step: 0.01 }}
                        fullWidth
                        required
                        autoFocus
                        helperText="Count all cash in the drawer"
                      />
                      <TextField
                        label="Notes (optional)"
                        value={sessionNotes}
                        onChange={e => setSessionNotes(e.target.value)}
                        multiline
                        rows={3}
                        fullWidth
                        placeholder="Any discrepancies or notes about this session..."
                      />
                    </Stack>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setCloseSessionDialog(false)}>Cancel</Button>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleCloseSession}
                      disabled={!actualCashAmount || sessionLoading}
                      startIcon={<LockClockIcon />}
                    >
                      Close Session
                    </Button>
                  </DialogActions>
                </Dialog>

                {/* Low stock alert dialog for header 'more' button */}
                <Dialog open={lowStockDialogOpen} onClose={() => setLowStockDialogOpen(false)} maxWidth="md" fullWidth>
                  <DialogTitle sx={{ fontWeight: 700, fontSize: 22, bgcolor: '#f5f6fa', borderBottom: '1px solid #eee' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box component="span" sx={{ width: 8, height: 32, bgcolor: 'warning.main', borderRadius: 1, mr: 2 }} />
                      All Low Stock Products
                    </Box>
                  </DialogTitle>
                  <DialogContent sx={{ bgcolor: '#fafbfc', p: 3 }}>
                    <Box sx={{ borderRadius: 2, boxShadow: 2, bgcolor: '#fff', p: 2 }}>
                      <Typography variant="subtitle1" fontWeight={600} mb={2} color="warning.main">
                        The following products are below their minimum stock threshold:
                      </Typography>
                      <Box sx={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ background: '#f5f6fa', borderBottom: '2px solid #eee' }}>
                              <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700 }}>Product Name</th>
                              <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700 }}>Brand</th>
                              <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700 }}>Category</th>
                              <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700 }}>Stock</th>
                              <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700 }}>Min Stock</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lowStockProducts.length === 0 ? (
                              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 24, color: '#888' }}>No low stock products.</td></tr>
                            ) : (
                              lowStockProducts.map(p => (
                                <tr key={p._id || p.name} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                  <td style={{ padding: '8px 12px', fontWeight: 600 }}>{p.name}</td>
                                  <td style={{ padding: '8px 12px' }}>{p.brand || '-'}</td>
                                  <td style={{ padding: '8px 12px' }}>{p.category || '-'}</td>
                                  <td style={{ padding: '8px 12px', textAlign: 'right', color: p.stock <= 0 ? '#d32f2f' : '#ed6c02', fontWeight: 700 }}>{p.stock}</td>
                                  <td style={{ padding: '8px 12px', textAlign: 'right', color: '#888' }}>{p.minStock ?? '-'}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </Box>
                    </Box>
                  </DialogContent>
                </Dialog>
                {tab === 'cashier' && (
                  <Cashier
                    products={products}
                    onSale={handleSaveSale}
                    loading={loading}
                    user={user}
                    currentSession={currentSession}
                  />
                )}
                {tab === 'sales' && (
                  <>
                    <SaleList
                      sales={sales}
                      loading={salesLoading}
                    />
                  </>
                )}
                {tab === 'products' && (
                  <ProductList
                    products={products}
                    loading={loading}
                    onAdd={handleAdd}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    token={getToken()}
                    onFavoriteToggle={() => fetchProducts()}
                  />
                )}
                {tab === 'products' && (
                  <ProductForm
                    open={formOpen}
                    onClose={() => setFormOpen(false)}
                    onSave={handleSave}
                    product={editProduct}
                    token={getToken()}
                  />
                )}
                {tab === 'unpaid' && (
                  <>
                    <UnpaidBills bills={unpaidBills} loading={loadingUnpaid} error={unpaidError} onUpdate={updateUnpaidBill} />
                    {editUnpaidOpen && (
                      <Dialog
                        open={editUnpaidOpen}
                        onClose={() => { setEditUnpaidOpen(false); setEditUnpaidBill(null); }}
                        maxWidth="lg"
                        fullWidth
                        PaperProps={{
                          sx: {
                            width: '90vw',
                            maxWidth: '1200px',
                          }
                        }}
                      >
                        <DialogTitle>Edit Unpaid Bill</DialogTitle>
                        <DialogContent>
                          <Box sx={{ mb: 2 }}>
                            <TextField
                              label="Search Products"
                              value={search}
                              onChange={e => setSearch(e.target.value)}
                              fullWidth
                              variant="outlined"
                              size="small"
                            />
                          </Box>
                          <Cashier
                            products={products}
                            user={user}
                            token={getToken()}
                            sale={editUnpaidBill}
                            onSale={handleSaveUnpaid}
                            loading={loading}
                            search={search}
                            editMode
                          />
                        </DialogContent>
                      </Dialog>
                    )}
                  </>
                )}
                {tab === 'transactions' && (
                  <Transactions token={getToken()} />
                )}
                {tab === 'cashier-sessions' && (
                  <CashierSessions token={getToken()} />
                )}
                {tab === 'customers' && (
                  <Customers user={user} />
                )}
                {tab === 'dealers' && (
                  <DealerList token={getToken()} />
                )}
                {tab === 'stock-purchase' && (
                  <StockPurchase user={user} onProductsUpdate={fetchProducts} />
                )}
                {tab === 'reports' && (
                  <Reports />
                )}
                {tab === 'settings' && (
                  <Settings user={user} onUserUpdate={setUser} />
                )}
              </CardContent>
            </Card>
          </Container>
          <Snackbar
            open={snackbar.open}
            autoHideDuration={3000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} variant="filled">
              {snackbar.message}
            </Alert>
          </Snackbar>
          
          {/* Invoice Dialog for completed unpaid bills */}
          <InvoiceDialog
            open={showInvoice}
            sale={invoiceSale}
            onClose={() => {
              setShowInvoice(false);
              setInvoiceSale(null);
            }}
            customerPending={null}
          />
        </Box>
      </Box>
    </>
  );
}

export default App;
